import {
  SkillDetailSchema,
  SkillListFilter,
  SkillListResponseSchema,
} from "@models";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import {
  baseSkillsQueryKey,
  getSkillQueryKey,
  getSkillsQueryKey,
} from "../../config/request-keys";
import { getSkill, getSkills } from "../../services";

export const invalidateCachedSkillQueries = (
  queryClient: QueryClient,
  orgId: string
) => {
  const skillsQueryKey = [...baseSkillsQueryKey, orgId];
  const skillsQuery = queryClient.getQueryCache().find({
    queryKey: skillsQueryKey,
    exact: false,
  });

  if (!skillsQuery) {
    return undefined;
  }

  return queryClient.invalidateQueries({ queryKey: skillsQueryKey });
};

export const useGetSkills = ({
  orgId,
  filter = "all",
  search = "",
  enabled = true,
}: {
  orgId: string;
  filter?: SkillListFilter;
  search?: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: getSkillsQueryKey(orgId, filter, search),
    queryFn: async () => {
      return (
        await SkillListResponseSchema.validate(
          (await getSkills({ orgId, filter, search })).data
        )
      ).skills;
    },
    enabled: enabled && !!orgId,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
};

export const useGetSkill = ({
  orgId,
  skillId,
  enabled = true,
}: {
  orgId: string;
  skillId: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: getSkillQueryKey(orgId, skillId),
    queryFn: async () => {
      return await SkillDetailSchema.validate(
        (await getSkill({ orgId, skillId })).data
      );
    },
    enabled: enabled && !!orgId && !!skillId,
  });
};
